import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary } from "@sentry/react";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { FontPreload } from "@/components/sheet/FontPreload";
import { PostHogProvider } from "posthog-js/react";
import { MotionConfig } from "framer-motion";
import { Toaster } from "@stringf/ui/sonner";
import SocketWrapper from "./SocketWrapper.tsx";
import { ThemeSync } from "./components/theme-sync.tsx";
import { routes } from "./routes.tsx";
import { buttonVariants } from "@stringf/ui/button";
import { isRefusedRequest } from "@stringf/services/api";
import { registerSessionHooks } from "@stringf/store/session";
import "./config-initializer.js";
import "../app/globals.css";
import "./i18n";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      // A 400/401/403 is the server's answer, not a hiccup: asking again gets the same one.
      retry: (failureCount, error) => failureCount < 1 && !isRefusedRequest(error),
    },
  },
});

export const router = createBrowserRouter(routes);

// The app's half of ending a session (`endSessionAtom`): the cache, push, the router.
registerSessionHooks({
  clearQueries: () => queryClient.clear(),
  // Imported on demand: push stays out of the entry bundle (lib/push.ts).
  unregisterPush: () => import("@/lib/push").then((m) => m.unregisterPush()),
  navigate: (to) => void router.navigate(to, { replace: true }),
  currentPath: () => router.state.location.pathname + router.state.location.search,
});

/*
  A new page starts at its top. The router leaves the window where it was, so a
  footer link (About, FAQ) opened the next page at its foot. Back and forward
  keep the browser's own restoration, and a link to a #section scrolls there.
*/
let shownPath = router.state.location.pathname;
router.subscribe(({ location, historyAction }) => {
  if (location.pathname === shownPath) return;
  shownPath = location.pathname;
  if (historyAction !== "POP" && !location.hash) window.scrollTo({ top: 0, behavior: "instant" });
});

const options = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
};

// The last-resort boundary: something outside the router failed. `globals.css`
// is imported by this module, so the token layer is always available here — no
// reason for the hand-written inline styles this used to carry.
function ErrorFallback() {
  return (
    <div className="min-h-screen bg-bg-0 flex flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="text-ui text-text-2">Something broke</p>
      <button
        onClick={() => window.location.reload()}
        className={buttonVariants({ variant: "outline", size: "sm" })}
      >
        Reload
      </button>
    </div>
  );
}

const ReactQueryDevtools =
  import.meta.env.DEV
    ? React.lazy(() =>
        import("@tanstack/react-query-devtools").then((m) => ({
          default: m.ReactQueryDevtools,
        }))
      )
    : () => null;

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary fallback={<ErrorFallback />}>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <FontPreload />
          <PostHogProvider
            apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
            options={options}
          >
            {/*
              reducedMotion="user" makes framer honour prefers-reduced-motion for
              every motion component under it: transforms are dropped, opacity is
              kept. One line, app-wide.

              This replaces hand-guarding. Before it, `useReducedMotion()` was
              called in exactly four carousel files while HowItWorks, LoopsSection,
              GetTheApp, CtaSection, Feat and faq.tsx all fired the same 22px
              `fadeUp` translate unguarded — which is the failure mode of per
              component guards: they are only ever added where someone remembered.

              The strands canvas is NOT covered: it is a 2D canvas, not a motion
              component, and gates itself.
            */}
            <MotionConfig reducedMotion="user">
              <RouterProvider router={router} />
            </MotionConfig>
          </PostHogProvider>
          <ThemeSync />
          <SocketWrapper />
          <Toaster />
        </HelmetProvider>
        {/* Not on /dev/app: its views own their QueryClients, and the bubble sat
            over the phone frames' tab bar. */}
        {import.meta.env.DEV && !window.location.pathname.startsWith("/dev/app") && (
          <React.Suspense>
            <ReactQueryDevtools initialIsOpen={false} />
          </React.Suspense>
        )}
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
